import { ChangeDetectorRef, Component, HostBinding } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_CHECKBOX_DEFAULT_OPTIONS, MatCheckboxDefaultOptions } from '@angular/material/checkbox';
import { TranslateService } from '@ngx-translate/core';
import { SeConstraintNotifierService } from '@se/constraint';
import { IControlType } from '@se/cp.common';
import { Logger } from '@se/se.ui.html5';
import { unescape } from 'lodash-es';
import { takeUntil } from 'rxjs/operators';
import { CpDurationControl, CpInputControl, PropertyType } from '../../cp-configuration/view-model';
import { CpModelService } from '../../cp-model.service';
import { AbstractControlComponent } from '../abstract-control';

const PATTERNS = {
    NUMERIC_DECIMAL: '[+\\-]?\\d+([\\.,]\\d+)?',
    NUMERIC_INTEGER: '[+\\-]?\\d+',
    MONEY: '[+\\-]?\\d+([\\.,]\\d{1,2})?',
};

@Component({
    selector: 'cp-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
    providers: [
        { provide: MAT_CHECKBOX_DEFAULT_OPTIONS, useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions },
    ],
})
export class CpInputComponent extends AbstractControlComponent {
    @HostBinding('class.stretched')
    isHostStretched!: boolean;

    control!: CpInputControl | CpDurationControl;

    patterns = PATTERNS;

    constructor(
        private modelService: CpModelService,
        private translate: TranslateService,
        cdr: ChangeDetectorRef,
        constraintNotifierService: SeConstraintNotifierService,
        logger: Logger,
    ) {
        super(cdr, constraintNotifierService, logger);
    }

    /**
     * Non manteniamo stato in questo componente, ma solo nel FormControl.
     */
    get model() {
        return this.formControl?.value;
    }

    get durationFormat() {
        if (!(this.control instanceof CpDurationControl)) {
            return {};
        }

        return this.control.format;
    }

    get durationLabel() {
        if (!(this.control instanceof CpDurationControl)) {
            return '';
        }
        return this.control.label;
    }

    onFocus() {
        this.focusin.emit();
    }

    onModelChange(model: string | number | null) {
        const changed = this.control.onModelChange(this.modelService, model, !this.isDisabled);
        this.cdr.detectChanges();

        return changed;
    }

    onCheckboxAction(threeState = false) {
        if (this.isDisabled) {
            return;
        }
        let value = this.formControl?.value ?? null;
        if (threeState) {
            if (value === null) {
                value = false;
            } else if (value) {
                value = null;
            } else {
                value = true;
            }
        } else {
            value = !value;
        }

        this.formControl?.setValue(value);
    }

    protected init() {
        this.isHostStretched = this.control?.controlType === IControlType.TextMultiLine;

        this.control.getEntityObservable(this.modelService).subscribe(this._entitySubject);

        this.formControl = new FormControl(this.control.getInitialValue(this.modelService), [
            this.makeValidator(this._entitySubject, this.control.dataBinding.propertyName, this.control.required),
        ]);

        this.control
            .getModelObservable(this.modelService)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((model) => {
                this.logger && this.logger.trace(`Model update ${this.control.name} ${model}`);
                this.updateFormControlValue(model);
            });

        if (this.control instanceof CpDurationControl) {
            this.control.init(this.translate);
        }
    }

    private updateFormControlValue(model: PropertyType | undefined) {
        if (!this.formControl) {
            return;
        }

        const formControlValue = this.formControl.value;

        const modelToSet = [IControlType.TextMultiLine, IControlType.TextSingleLine].includes(this.control.controlType)
            ? this.removeHTML(model)
            : model;

        // Non aggiornare il campo se i valori sono uguali.
        if (!modelToSet && !modelToSet === !formControlValue) {
            return;
        }
        this.formControl.setValue(modelToSet);
    }

    private removeHTML = (html: PropertyType | undefined): string => {
        if (html === undefined || html === null || html === '') return '';
        else html = html.toString();
        return unescape(html.replace(/(<([^>]+)>)/gi, ''));
    };
}
